import {
  PopulationFilter,
  PopulationFilterCondition,
} from '@digital-twins/shared-types'
import { CUT_DIRECTIONAL_N } from '@digital-twins/deck-ui/lib/aggregation'

import { cleanSegmentLabel } from '@/components/quick-study-results/respondentNoun'
import i18n from '@/i18n'
import { humanizeColumnName } from '@/lib/utils/column-labels'

/**
 * The review panel's "who answers" chips: one per top-level condition of the
 * run's population filter, the Assistant's `run_workflow` filter as the run
 * pinned it. Removing a chip sends the filter that is left to
 * `PATCH /workflow-runs/:token/segment`; removing the last one clears it.
 *
 * Generic over any filter: a chip names its values, not a template's fields.
 */

/** Mirrors `TWIN_TEST_MIN_PANEL_SIZE` in the backend's twin-test constants. */
export const QUICK_STUDY_MIN_PANEL = 10

/**
 * Below this many respondents, a two-way breakdown leaves each group under the
 * directional cut floor (`CUT_DIRECTIONAL_N`), so the gate warns before spend.
 */
export const QUICK_STUDY_THIN_BREAKDOWN_PANEL = CUT_DIRECTIONAL_N * 2

/** Mirrors `MaxLength(255)` on the segment DTO, so a chip removal never 400s. */
export const SEGMENT_LABEL_MAX_LENGTH = 255

export interface SegmentChip {
  /** Position in the filter's top-level `conditions`. */
  index: number
  label: string
}

function isFilterGroup(
  entry: PopulationFilterCondition | PopulationFilter,
): entry is PopulationFilter {
  return Array.isArray((entry as PopulationFilter).conditions)
}

function valueList(value: PopulationFilterCondition['value']): Array<string> {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean)
  }
  if (value === undefined || value === null) {
    return []
  }
  const text = String(value).trim()
  return text ? [text] : []
}

function joinOr(values: Array<string>): string {
  return values.join(i18n.t('threads.detail.gate.segment.or', { ns: 'app' }))
}

function groupValues(condition: PopulationFilterCondition): Array<string> {
  if (condition.memberValues) {
    return Object.values(condition.memberValues).flat()
  }
  if (condition.options && condition.options.length > 0) {
    return condition.options.map(humanizeColumnName)
  }
  if (condition.members && condition.members.length > 0) {
    return condition.members.map(humanizeColumnName)
  }
  return valueList(condition.value)
}

/** One condition as a chip reads it: its values, or a field plus an operator phrase. */
export function describeSegmentCondition(
  entry: PopulationFilterCondition | PopulationFilter,
): string {
  if (isFilterGroup(entry)) {
    const parts = (entry.conditions ?? []).map(describeSegmentCondition).filter(Boolean)
    return entry.logic === 'OR' ? joinOr(parts) : parts.join(', ')
  }

  const field = humanizeColumnName(entry.field)
  const values = entry.groupType ? groupValues(entry) : valueList(entry.value)
  const value = joinOr(values)

  switch (entry.operator) {
    case 'not_equals':
    case 'not_in':
      return i18n.t('threads.detail.gate.segment.notValue', { value, ns: 'app' })
    case 'greater_than':
      return i18n.t('threads.detail.gate.segment.greaterThan', { field, value, ns: 'app' })
    case 'less_than':
      return i18n.t('threads.detail.gate.segment.lessThan', { field, value, ns: 'app' })
    case 'contains':
      return i18n.t('threads.detail.gate.segment.contains', { field, value, ns: 'app' })
    case 'not_contains':
      return i18n.t('threads.detail.gate.segment.notContains', { field, value, ns: 'app' })
    case 'is_empty':
      return i18n.t('threads.detail.gate.segment.isEmpty', { field, ns: 'app' })
    case 'is_not_empty':
      return i18n.t('threads.detail.gate.segment.isNotEmpty', { field, ns: 'app' })
    default:
      return value || field
  }
}

export function segmentChipsFromFilter(
  filter: PopulationFilter | null | undefined,
): Array<SegmentChip> {
  const conditions = filter?.conditions ?? []
  return conditions
    .map((entry, index) => ({
      index,
      label: cleanSegmentLabel(describeSegmentCondition(entry)),
    }))
    .filter((chip) => chip.label.length > 0)
}

/** The filter with one top-level condition removed; null once nothing is left. */
export function filterWithoutChip(
  filter: PopulationFilter | null | undefined,
  index: number,
): PopulationFilter | null {
  const conditions = filter?.conditions ?? []
  const remaining = conditions.filter((_, position) => position !== index)
  if (remaining.length === 0) {
    return null
  }
  return { logic: filter?.logic ?? 'AND', conditions: remaining }
}

/** The label a narrowed filter carries: what its remaining chips say. */
export function segmentLabelFromChips(chips: Array<SegmentChip>): string {
  const label = chips.map((chip) => chip.label).join(', ')
  if (label.length <= SEGMENT_LABEL_MAX_LENGTH) {
    return label
  }
  return `${label.slice(0, SEGMENT_LABEL_MAX_LENGTH - 1).trimEnd()}\u2026`
}

export type SegmentGateNotice =
  | { kind: 'below_minimum'; count: number }
  | { kind: 'thin_breakdowns'; count: number }
  | { kind: 'count_failed' }

export interface SegmentGateInput {
  /** The run carries a population filter. A full-audience run has no notice. */
  hasFilter: boolean
  isCounting: boolean
  /** Live matching count; null after a failed preview. */
  matchingCount: number | null
  /** The panel the step asked for; null when unknown. */
  requestedPanel: number | null
}

/**
 * What the gate says about the panel a filtered run can actually draw:
 * - the count could not be read: blocks (nothing honest to quote);
 * - effective panel min(requested, matching) below the minimum: blocks;
 * - the segment cut the panel below the breakdown floor: warns. A panel the
 *   step itself asked to be small is not the segment's doing and says nothing.
 * Nothing while counting (Approve is held separately) or for a full audience.
 */
export function segmentGateNotice(input: SegmentGateInput): SegmentGateNotice | null {
  const { hasFilter, isCounting, matchingCount, requestedPanel } = input
  if (!hasFilter || isCounting) {
    return null
  }
  if (typeof matchingCount !== 'number' || !Number.isFinite(matchingCount)) {
    return { kind: 'count_failed' }
  }
  const effective =
    typeof requestedPanel === 'number'
      ? Math.min(requestedPanel, matchingCount)
      : matchingCount
  if (effective < QUICK_STUDY_MIN_PANEL) {
    return { kind: 'below_minimum', count: effective }
  }
  const isCutBySegment =
    typeof requestedPanel !== 'number' || matchingCount < requestedPanel
  if (effective < QUICK_STUDY_THIN_BREAKDOWN_PANEL && isCutBySegment) {
    return { kind: 'thin_breakdowns', count: effective }
  }
  return null
}

/** Approve is held while a filtered run is counting, and refused on a blocking notice. */
export function isSegmentApprovalBlocked(input: SegmentGateInput): boolean {
  if (!input.hasFilter) {
    return false
  }
  if (input.isCounting) {
    return true
  }
  const notice = segmentGateNotice(input)
  return notice?.kind === 'below_minimum' || notice?.kind === 'count_failed'
}
